import type {AppNotification, ChatHint} from "~/types/Notifications";

// Tied to ChatHint['action'] so adding a hint kind fails to compile until it is routed.
const CHAT_HINT_ACTIONS: ChatHint['action'][] = ['chat-message', 'chat-reaction', 'chat-message-deleted'];

// `action` is optional on the payload — a notification carrying none at all must simply not
// match, rather than being narrowed by an assertion that quietly claims it did.
const isChatHintAction = (action?: string): action is ChatHint['action'] =>
    CHAT_HINT_ACTIONS.some(known => known === action);

export const useNotificationsStore = defineStore('NotificationsStore', () => {

    const {$api} = useNuxtApp();
    const { userLoggedIn } = useWadAuth();

    const fcmToken = ref<string | null>(null);
    // True while a token is being (re)acquired from Firebase, so the UI can show a
    // pending/disabled state instead of a misleading "off" until the response arrives.
    const fcmTokenResolving = ref<boolean>(false);
    const newTokenRequested = ref<boolean>(false);
    const modalEnableNotificationsClosed = ref<boolean>(false);
    const modalReceiveNotificationsClosed = ref<boolean>(false);
    const disabledPushNotificationsWarningClosed = ref<boolean>(false);
    const unsupportedPushNotificationsWarningClosed = ref<boolean>(false);
    const notifyOtherDevices = ref<boolean>(false);
    const pauseNotificationsPageLogic = ref<boolean>(false);

    const clearFcmToken = () => {
        fcmToken.value = null;
    }

    //region User Notifications


    const userNotifications = ref<AppNotification[]>([]);

    /**
     * Badge state, kept separately from the list. The API caps the list, so counting unread
     * entries in `userNotifications` would miss anything older than the cap - and then the
     * mark-as-read call in clientNavigation, which is gated on this, would never fire for it.
     *
     * Three rules, and only these three: set from the API's meta on load, zeroed once a
     * mark-as-read load succeeds, and incremented for a notification that arrives over SSE
     * (which is added to the array locally and so is in no API response yet).
     *
     * On an API that does not send the count it degrades to counting the list, which is what this
     * was before the count existed. That matters because the two repos deploy separately: with a
     * plain `?? 0` a frontend-first release would leave the badge permanently dark AND, because
     * clientNavigation gates the mark-as-read call on it, stop anything ever being marked read -
     * so every notification would resurface as unread once the API caught up.
     */
    const unreadNotificationCount = ref<number>(0);
    const hasUnreadNotifications = computed(() => unreadNotificationCount.value > 0);

    const loadNotifications = async (markAsRead: boolean = false) => {
        if(!userLoggedIn.value) {
            userNotifications.value = [];
            unreadNotificationCount.value = 0;
            return;
        }
        const response = await $api.v2.getUserNotifications(markAsRead);
        userNotifications.value = response?.data ?? [];
        // The server counts after its own update, so a mark-as-read load already reports 0. Kept
        // explicit so the badge cannot survive a successful mark-as-read on an older API.
        // The list is already assigned above, so the fallback counts what this response returned.
        unreadNotificationCount.value = markAsRead
            ? 0
            : (response?.meta?.unread_count ?? userNotifications.value.filter(notification => !notification.read).length);
    }

    /**
     * The conversation whose thread is on screen, registered by useEventDmChat for as long as
     * it is mounted. Deliberately not persisted — it describes this render, nothing more.
     */
    const openChatConversationId = ref<number | null>(null);

    const setOpenChatConversation = (conversationId: number | null) => {
        openChatConversationId.value = conversationId;
    }

    /**
     * Mirror locally the read the API performs whenever it hands over a thread's messages
     * (ChatService::openConversation and ::listMessages). Without it a user reads the thread,
     * the server clears the notification, and the header dot plus the list entry pointing at
     * that very thread survive until the next notifications load — which, since the normal way
     * into a thread is the attendee page, may not happen for the rest of the event.
     *
     * Only the list is searched, and the count is decremented by what was found rather than
     * assumed: the API caps the list, and guessing would be the one way to drive the badge
     * negative. That cap costs nothing here — notifyNewMessage() coalesces a conversation onto
     * a single row and bumps its send_at on every message, so an unread chat notification is
     * always among the newest and cannot have fallen off the end.
     */
    const markChatConversationRead = (conversationId: number) => {
        // Selected before anything is written: open() calls this on every thread open, and the
        // usual answer is "nothing matched" — which should cost no write at all, rather than
        // re-rendering the list for every subscriber.
        const cleared = userNotifications.value.filter(notification => !notification.read
            && notification.data?.type === 'chat'
            && notification.data?.conversation_id === conversationId);

        if(!cleared.length) return;

        for(const notification of cleared) {
            notification.read = true;
        }

        unreadNotificationCount.value = Math.max(0, unreadNotificationCount.value - cleared.length);
    }

    const handleNewSseEvent = (notification: AppNotification) => {
        if(notification.type === wadConstants.NOTIFICATIONS.TYPE.TECHNICAL) {
            if(notification.data?.action === 'reload-events') {
                // Forwarded whole, including the optional `event_id`: only the handler in app.vue
                // knows the client's current event, so the per-event scoping is applied there.
                window.dispatchEvent(new CustomEvent('wad:sse-technical-message', { detail: notification.data }));
            }else if(notification.data?.action === 'update-counts') {
                window.dispatchEvent(new CustomEvent('wad:sse-technical-message', { detail: notification.data }));
            }else if(notification.data?.action === 'reload-bookmarks') {
                window.dispatchEvent(new CustomEvent('wad:sse-technical-message', { detail: notification.data }));
            }else if(notification.data?.action === 'enabled-push-notifications') {
                window.dispatchEvent(new CustomEvent('wad:push-notifications-enabled'));
            }else if(notification.data?.action === 'disabled-push-notifications') {
                window.dispatchEvent(new CustomEvent('wad:push-notifications-disabled'));
            }else if(notification.data?.action === 'reload-permissions') {
                window.dispatchEvent(new CustomEvent('wad:sse-technical-message', { detail: notification.data }));
            }else if(isChatHintAction(notification.data?.action)) {
                // Ids only — SSE topics are md5(userId) on a hub that accepts anonymous
                // subscribers, so message content never travels this way. An open thread
                // takes the hint and fetches over the authenticated API.
                //
                // Its own event rather than wad:sse-technical-message so the detail is
                // typed and a listener can ignore hints for other conversations.
                window.dispatchEvent(new CustomEvent<ChatHint>('wad:chat-hint', { detail: notification.data as ChatHint }));
            }
        } else {
            // A chat notification for the thread already on screen describes a message the user
            // is looking at, and the API has treated it as read since the backfill that fetched
            // it. The row is written before the hint is broadcast, so its fanout regularly lands
            // after that read — counting it would put the dot back for a message already seen.
            // The service worker applies the same rule to pushes, matching on URL.
            const readOnArrival = notification.data?.type === 'chat'
                && notification.data?.conversation_id === openChatConversationId.value;
            const arriving = readOnArrival ? {...notification, read: true} : notification;

            userNotifications.value.unshift(arriving);
            // Not part of any API response, so the badge has to be bumped here - otherwise the
            // header dot stays dark until the next load. Read off the row that was actually
            // stored, so the badge cannot disagree with the list.
            if(!arriving.read) {
                unreadNotificationCount.value++;
            }
            if(notification.type === wadConstants.NOTIFICATIONS.TYPE.EVENT_ATTENDEE_ACCEPTED_CONNECTION || notification.type === wadConstants.NOTIFICATIONS.TYPE.EVENT_ATTENDEE_NEW_CONNECTION) {
                if(notification.url === `${window.location.origin}${window.location.pathname}`) {
                    window.dispatchEvent(new CustomEvent('wad:sse-update-page'));
                }
            }
        }
    }


    //endregion

    return {
        fcmToken,
        fcmTokenResolving,
        newTokenRequested,
        modalEnableNotificationsClosed,
        modalReceiveNotificationsClosed,
        disabledPushNotificationsWarningClosed,
        unsupportedPushNotificationsWarningClosed,
        notifyOtherDevices,
        pauseNotificationsPageLogic,
        clearFcmToken,
        // User notifications
        userNotifications,
        unreadNotificationCount,
        hasUnreadNotifications,
        loadNotifications,
        markChatConversationRead,
        setOpenChatConversation,
        handleNewSseEvent
    }
}, {
    persist: {
        pick: [
            'fcmToken',
            'newTokenRequested',
            'modalReceiveNotificationsClosed',
            'modalEnableNotificationsClosed',
            'disabledPushNotificationsWarningClosed',
            'unsupportedPushNotificationsWarningClosed'
        ]
    },
},)
